import React, { useState } from "react";
import { useHistory } from "react-router-dom";
import { makeStyles, Typography, Paper, Box, LinearProgress, Button } from "@material-ui/core";
import CheckIcon from "@material-ui/icons/Check";
import SmartphoneIcon from "@material-ui/icons/Smartphone";
import ChatIcon from "@material-ui/icons/Chat";
import AccountTreeIcon from "@material-ui/icons/AccountTree";
import PeopleAltIcon from "@material-ui/icons/PeopleAlt";
import CallSplitIcon from "@material-ui/icons/CallSplit";
import FlashOnIcon from "@material-ui/icons/FlashOn";
import LocalOfferIcon from "@material-ui/icons/LocalOffer";
import LoyaltyRoundedIcon from "@material-ui/icons/LoyaltyRounded";
import { AllInclusive, Event, Schedule, Forum, Lock, Send, SentimentSatisfiedAlt } from "@material-ui/icons";
import { FaRocket } from "react-icons/fa";
import MainContainer from "../../components/MainContainer";
import { i18n } from "../../translate/i18n";
import useGettingStarted from "../../hooks/useGettingStarted";
import GuideFloat from "../../components/GuideFloat";
import GuideWalk from "../../components/GuideWalk";
import { guideImage, guidePlanHint } from "../../content/guidePlan";
import { guideSteps } from "../../content/guideDetails";
import { connectionWalk } from "../../content/connectionWalk";
import { TINT, RADII, BORDERS } from "../../theme/redesign";

const ICONS = {
  phone: SmartphoneIcon,
  chat: ChatIcon,
  tree: AccountTreeIcon,
  people: PeopleAltIcon,
  split: CallSplitIcon,
  flash: FlashOnIcon,
  tag: LocalOfferIcon,
  kanban: LoyaltyRoundedIcon,
  ai: AllInclusive,
  send: Send,
  schedule: Schedule,
  forum: Forum,
  event: Event,
  lock: Lock,
  csat: SentimentSatisfiedAlt,
};

const useStyles = makeStyles((theme) => ({
  root: {
    height: "100%",
    overflowY: "auto",
    overflowX: "hidden",
    padding: theme.spacing(1, 1, 4, 0.5),
    [theme.breakpoints.up("md")]: {
      padding: theme.spacing(2, 3, 5, 1),
    },
  },
  hello: {
    fontSize: "1.75rem",
    fontWeight: 800,
    letterSpacing: -0.4,
    marginBottom: theme.spacing(2.5),
    color: theme.palette.text.primary,
  },
  setupCard: {
    borderRadius: 18,
    border: BORDERS.base(theme),
    boxShadow: "none",
    backgroundColor: theme.palette.background.paper,
    padding: theme.spacing(2.5, 2.5, 1.5),
    marginBottom: theme.spacing(3),
  },
  setupHead: {
    display: "flex",
    alignItems: "flex-start",
    gap: 14,
    marginBottom: theme.spacing(2),
  },
  rocket: {
    width: 48,
    height: 48,
    borderRadius: 14,
    flexShrink: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#fff",
    backgroundColor: theme.palette.primary.main,
    fontSize: 26,
  },
  setupTitle: {
    fontWeight: 800,
    fontSize: "1.15rem",
    lineHeight: 1.2,
  },
  setupSub: {
    color: theme.palette.text.secondary,
    fontSize: 14,
    marginTop: 2,
  },
  progressRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    marginBottom: 8,
  },
  nextLabel: {
    fontSize: 13,
    color: theme.palette.text.secondary,
    "& strong": {
      color: theme.palette.text.primary,
      fontWeight: 700,
    },
  },
  count: {
    fontSize: 13,
    fontWeight: 700,
    color: "#16a34a",
    whiteSpace: "nowrap",
  },
  bar: {
    height: 7,
    borderRadius: 999,
    backgroundColor: theme.mode === "dark" ? "#1f2937" : "#e8f5e9",
    marginBottom: theme.spacing(1.5),
    "& .MuiLinearProgress-bar": {
      borderRadius: 999,
      backgroundColor: "#22c55e",
    },
  },
  step: {
    display: "flex",
    alignItems: "flex-start",
    flexWrap: "wrap",
    gap: 14,
    padding: "14px 6px",
    borderTop: BORDERS.base(theme),
    cursor: "pointer",
    borderRadius: 10,
    transition: "background-color 120ms ease",
    "&:hover": {
      backgroundColor: theme.mode === "dark" ? "rgba(129,140,248,0.08)" : "#f8fafc",
    },
  },
  stepDone: {
    cursor: "pointer",
    "& $stepTitle, & $stepDesc": {
      color: theme.palette.text.disabled,
      textDecoration: "line-through",
      textDecorationThickness: 1,
    },
  },
  bubble: {
    width: 28,
    height: 28,
    borderRadius: "50%",
    border: `2px solid ${theme.mode === "dark" ? "#475569" : "#d1d5db"}`,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  bubbleDone: {
    border: "none",
    backgroundColor: "#2563eb",
    color: "#fff",
  },
  stepIcon: {
    color: theme.palette.text.secondary,
    fontSize: 22,
    flexShrink: 0,
  },
  stepBody: { flex: 1, minWidth: 150 },
  stepTitle: {
    fontWeight: 700,
    fontSize: 15,
  },
  stepDesc: {
    fontSize: 13,
    color: theme.palette.text.secondary,
    marginTop: 2,
  },
  chevron: {
    color: theme.palette.text.disabled,
    fontSize: 14,
  },
  extrasTitle: {
    fontWeight: 800,
    fontSize: "1.2rem",
    marginBottom: 4,
  },
  extrasSub: {
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(2),
    fontSize: 14,
  },
  extrasGrid: {
    display: "grid",
    gridTemplateColumns: "1fr",
    gap: 12,
    [theme.breakpoints.up("sm")]: {
      gridTemplateColumns: "1fr 1fr",
    },
  },
  extraCard: {
    borderRadius: RADII.surface,
    border: BORDERS.base(theme),
    boxShadow: "none",
    padding: theme.spacing(2),
    cursor: "pointer",
    backgroundColor: TINT.brand(theme),
    display: "flex",
    gap: 14,
    alignItems: "flex-start",
    minWidth: 0,
    transition: "transform 120ms ease, box-shadow 120ms ease",
    "&:hover": {
      transform: "translateY(-1px)",
    },
  },
  extraWide: {
    [theme.breakpoints.up("sm")]: {
      gridColumn: "1 / -1",
    },
  },
  extraIcon: {
    width: 44,
    height: 44,
    borderRadius: 12,
    backgroundColor: theme.palette.primary.main,
    color: "#fff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    "& svg": { fontSize: 22 },
  },
  guide: {
    fontSize: 13, lineHeight: 1.6, marginTop: 10,
    "& summary": { cursor: "pointer", color: theme.palette.primary.main, fontWeight: 600 },
    "& ol": { paddingLeft: 22 },
    "& li": { marginBottom: 8 },
  },
  extraTitle: {
    fontWeight: 800,
    fontSize: 16,
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
  extraDesc: {
    fontSize: 13,
    color: theme.palette.text.secondary,
    marginTop: 4,
    lineHeight: 1.4,
  },
  newBadge: {
    fontSize: 11,
    fontWeight: 800,
    color: "#fff",
    backgroundColor: theme.palette.primary.main,
    borderRadius: 999,
    padding: "2px 8px",
  },
}));

const GettingStarted = () => {
  const classes = useStyles();
  const history = useHistory();
  const { loading, planError, refresh, plan, steps, extras, done, total, nextStep, userName } = useGettingStarted();
  const [guide, setGuide] = useState(null);
  const locale = (i18n.language || "es").slice(0, 2);

  const go = (to) => {
    if (to) history.push(to);
  };

  const openGuide = (item) => {
    const base = i18n.t(item.instructionsKey, { returnObjects: true });
    setGuide({
      id: item.id,
      title: i18n.t(item.titleKey),
      description: i18n.t(item.descKey),
      steps: guideSteps(locale, item.id, base),
      to: item.to,
    });
  };

  return (
    <MainContainer>
      <div className={classes.root}>
        <Typography className={classes.hello}>
          {i18n.t("gettingStarted.hello", { name: userName })}
        </Typography>

        {loading && <Typography role="status">{i18n.t("gettingStarted.loading")}</Typography>}
        {planError && <Paper style={{ padding: 16 }}>
          <Typography role="alert">{i18n.t("gettingStarted.planError")}</Typography>
          <Button color="primary" onClick={refresh}>{i18n.t("gettingStarted.retry")}</Button>
        </Paper>}
        {steps.length > 0 && (
          <Paper className={classes.setupCard} elevation={0}>
            <div className={classes.setupHead}>
              <div className={classes.rocket}>
                <FaRocket />
              </div>
              <div>
                <Typography className={classes.setupTitle}>
                  {i18n.t("gettingStarted.title")}
                </Typography>
                <Typography className={classes.setupSub}>
                  {i18n.t("gettingStarted.subtitle")}
                </Typography>
              </div>
            </div>
            <div className={classes.progressRow}>
              <Typography className={classes.nextLabel}>
                {nextStep
                  ? (
                    <>
                      {i18n.t("gettingStarted.next")}{" "}
                      <strong>{i18n.t(nextStep.titleKey)}</strong>
                    </>
                  )
                  : i18n.t("gettingStarted.allDone")}
              </Typography>
              <span className={classes.count}>
                {loading ? "…" : i18n.t("gettingStarted.completed", { done, total })}
              </span>
            </div>
            <LinearProgress
              className={classes.bar}
              variant="determinate"
              value={total ? (done / total) * 100 : 0}
            />
            {steps.map((step) => {
              const Icon = ICONS[step.icon] || ChatIcon;
              return (
                <div
                  key={step.id}
                  className={`${classes.step} ${step.done ? classes.stepDone : ""}`}
                  onClick={() => openGuide(step)}
                  onKeyDown={(event) => { if (event.key === "Enter") openGuide(step); }}
                  role="button"
                  tabIndex={0}
                >
                  <span className={`${classes.bubble} ${step.done ? classes.bubbleDone : ""}`}>
                    {step.done ? <CheckIcon style={{ fontSize: 16 }} /> : null}
                  </span>
                  <Icon className={classes.stepIcon} />
                  <div className={classes.stepBody}>
                    <div className={classes.stepTitle}>{i18n.t(step.titleKey)}</div>
                    <div className={classes.stepDesc}>
                      {i18n.t(step.descKey)} · {i18n.t(step.timeKey)}
                    </div>
                  </div>
                  <Button color="primary" size="small" onClick={() => openGuide(step)}>{i18n.t("gettingStarted.guide")}</Button>
                </div>
              );
            })}
          </Paper>
        )}

        {extras.length > 0 && (
          <>
            <Typography className={classes.extrasTitle}>
              {i18n.t("gettingStarted.extrasTitle")}
            </Typography>
            <Typography className={classes.extrasSub}>
              {i18n.t("gettingStarted.extrasSubtitle")}
            </Typography>
            <div className={classes.extrasGrid}>
              {extras.map((card) => {
                const Icon = ICONS[card.icon] || FlashOnIcon;
                return (
                  <Paper
                    key={card.id}
                    elevation={0}
                    className={`${classes.extraCard} ${card.wide ? classes.extraWide : ""}`}
                    onClick={() => openGuide(card)}
                    onKeyDown={(event) => { if (event.key === "Enter") openGuide(card); }}
                    role="button"
                    tabIndex={0}
                  >
                    <div className={classes.extraIcon}>
                      <Icon />
                    </div>
                    <Box minWidth={0}>
                      <div className={classes.extraTitle}>
                        {i18n.t(card.titleKey)}
                        {card.badgeKey ? (
                          <span className={classes.newBadge}>{i18n.t(card.badgeKey)}</span>
                        ) : null}
                      </div>
                      <div className={classes.extraDesc}>{i18n.t(card.descKey)}</div>
                      <Button color="primary" size="small" onClick={() => openGuide(card)}>{i18n.t("gettingStarted.guide")}</Button>
                    </Box>
                  </Paper>
                );
              })}
            </div>
          </>
        )}
        <GuideWalk
          open={guide?.id === "connection" || guide?.id === "assignment"}
          title={guide?.title}
          planHint={guide ? guidePlanHint(plan, guide.id, locale) : ""}
          steps={connectionWalk(locale, plan)}
          startId={guide?.id === "assignment" ? "routing" : "buttons"}
          closeLabel={i18n.t("gettingStarted.close")}
          openLabel={i18n.t("gettingStarted.open")}
          onClose={() => setGuide(null)}
          onOpen={guide?.to ? () => { const to = guide.to; setGuide(null); go(to); } : null}
          onNavigate={(to) => { setGuide(null); go(to); }}
        />
        <GuideFloat
          open={!!guide && guide.id !== "connection" && guide.id !== "assignment"}
          title={guide?.title}
          description={guide?.description}
          steps={guide?.steps}
          image={guide ? guideImage(guide.id) : ""}
          imageCaption={i18n.t("gettingStarted.imageCaption")}
          planHint={guide ? guidePlanHint(plan, guide.id, locale) : ""}
          closeLabel={i18n.t("gettingStarted.close")}
          openLabel={i18n.t("gettingStarted.open")}
          onClose={() => setGuide(null)}
          onOpen={guide?.to ? () => { const to = guide.to; setGuide(null); go(to); } : null}
        />
      </div>
    </MainContainer>
  );
};

export default GettingStarted;
